import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "dayjs";
import Box from "utils/Box";
import Grid from "utils/Grid";
import { Typography, Button, Card } from "@mui/material";
import ContentHeader from "../common/ContentHeader";
import TableGenerator from "../../../../../Common/TableGenerator";
import { ExportButton } from "../common/Buttons";
import Label from "../common/Label";
import { useNavigate } from "react-router";
import styles from "./styles";
import _ from "lodash";
import { useSnackbar } from "notistack";
import _flatten from "lodash/flatten";
import { useSelector } from "react-redux";
import useDevice from "../hooks/useDevice";
import { handleServerErrors } from "helpers";
import { MSeriesModulesDropDown } from "../../../../../Common";

const map = {
  in_stock: {
    text: "In Stock",
    color: "success",
  },
  out_of_stock: {
    text: "Out of Stock",
    color: "error",
  },
  limited: {
    text: "Limited",
    color: "warning",
  },
};

const InventoryPerProduct = () => {
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [modulesSelected, setModulesSelected] = useState([]);
  const [deviceId] = useState(location?.state?.oro_id);
  const [deviceTypeList, setDeviceList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [mSeriesModules, setMSeriesModules] = useState([]);
  const [mSeriesSelectedModule, setMSeriesSelectedModule] = useState(null);
  const [inventoriesList, setInventoriesList] = useState([]);
  const [planogramsList, setPlanogramsList] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const { device } = useDevice(deviceId);
  const current_user = useSelector((state) => state.userReducer.current_user);
  const navigate = useNavigate();
  const classes = styles();

  const fields = [
    {
      key: "product_id",
      columnName: "Product Id",
      render: (_, val) => val.product_id || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },
    {
      key: "product_sku",
      columnName: "Product SKU",
      render: (_, val) => val.product_sku || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },
    {
      key: "product_name",
      columnName: "Product Name",
      render: (_, val) => val.product_name || "---",
      type: "text",
      visible: true,
      disableSorting: true,
    },
    {
      key: "stock",
      columnName: "Stock",
      render: (_, val) => `${val.stock || 0}`,
      type: "text",
      visible: true,
      disableSorting: true,
    },
    // --- NEW: Total Capacity Column ---
    {
      key: "total_capacity",
      columnName: "Total Capacity",
      render: (_, val) => `${val.total_capacity || 0}`,
      type: "text",
      visible: true,
      disableSorting: true,
    },
    // ----------------------------------
    {
      key: "inventory",
      columnName: "Sold-out Status",
      type: "text",
      visible: true,
      // Fixed to use total_capacity so the % calculation is accurate across multiple racks
      render: (_, value) => (
        <>{getInventoryLabel(value.stock, value.total_capacity)}</>
      ),
      disableSorting: true,
    },
  ];

  const getLabel = (stock, capacity) => {
    if (stock == 0 || capacity == 0) {
      return "out_of_stock";
    } else if (stock <= 30) {
      return "limited";
    } else {
      return "in_stock";
    }
  };

  const getInventoryLabel = (inventory, capacity, label) => {
    // Prevent NaN if capacity is 0
    const stockPercentage = capacity > 0 ? (100 * inventory) / capacity : 0; 
    let inventoryType = getLabel(stockPercentage, capacity);
    const { text, color } = map[inventoryType];

    if (label) {
      return text || "0";
    } else {
      return <Label color={color}>{text}</Label>;
    }
  };

  const getInventoryDetailsFromServer = async (
    module_count = 1,
    selectedModule
  ) => {
    try {
      const { data: inventoryData } = await window.axiosIns(
        `/device-data/${deviceId}/com.digitalmediavending.Inventory/?limit=${module_count}`
      );

      const inventoriesData = inventoryData?.data?.property || [];
      setInventoriesList(inventoriesData);

      let inventory_details;
      if (selectedModule) {
        const moduleInventory =
          inventoriesData.find((item) => item.moduleNumber === selectedModule)
            ?.inventoryDetails || "[]";
        inventory_details = JSON.parse(moduleInventory);
      } else {
        inventory_details = JSON.parse(
          inventoriesData[0]?.inventoryDetails || "[]"
        );
      }

      const { data: planogramData } = await window.axiosIns.get("/planogram", {
        params: {
          cabinet_id__vm_id__oro_id: deviceId,
        },
      });

      const planogramsList = planogramData?.results || [];
      setPlanogramsList(planogramsList);

      let planogram_details;

      if (selectedModule) {
        planogram_details =
          planogramsList.find((item) => item.module_number === selectedModule)
            .details || [];
      } else {
        planogram_details = planogramsList?.[0]?.details || [];
      }

      // Ensure we extract capacity from the inventory details as well
      _.forEach(inventory_details, ({ col, row, stock, capacity }) => {
        const index = _.findIndex(
          planogram_details,
          (x) => x.col == col && x.row == row
        );

        if (index > -1) {
          const products = planogramData?.results?.[0]?.product_details || [];
          const prd = _.find(
            products,
            (x) => x.id === planogram_details[index]?.product_id
          );
          const product_sku = prd?.product_sku;
          const shelf_life = prd?.shelf_life;
          planogram_details[index] = {
            ...planogram_details[index],
            stock,
            capacity: capacity || planogram_details[index].capacity || 0, // Fallback to planogram capacity
            shelf_life,
            product_sku,
          };
        }
      });

      const productData = {};
      const capacityData = {}; // Track capacity sums
      const productList = planogram_details.filter((x) => x.product_id);
      
      const tableData = productList.map((x) => {
        productData[x.product_id] = (productData[x.product_id] || 0) + (x.stock || 0);
        capacityData[x.product_id] = (capacityData[x.product_id] || 0) + parseInt(x.capacity || 0);
        return { 
          ...x, 
          stock: productData[x.product_id], 
          total_capacity: capacityData[x.product_id] // Save the running total
        };
      });
      const result = _.reverse(
        _.uniqBy(_.reverse([...tableData]), "product_id")
      );
      setDeviceList(result);
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };

  const getInventoryDetails = async (selectedModule) => {
    const moduleInventory =
      inventoriesList.find((item) => item.moduleNumber === selectedModule)
        ?.inventoryDetails || "[]";
    const inventory_details = JSON.parse(moduleInventory);

    const modulePlanogram = planogramsList.find(
      (item) => item.module_number === selectedModule
    );
    const planogram_details = modulePlanogram?.details || [];

    // Ensure we extract capacity here too
    _.forEach(inventory_details, ({ col, row, stock, capacity }) => {
      const index = _.findIndex(
        planogram_details,
        (x) => x.col == col && x.row == row
      );

      if (index > -1) {
        const products = modulePlanogram?.product_details || [];
        const prd = _.find(
          products,
          (x) => x.id === planogram_details[index]?.product_id
        );
        const product_sku = prd?.product_sku;
        const shelf_life = prd?.shelf_life;
        planogram_details[index] = {
          ...planogram_details[index],
          stock,
          capacity: capacity || planogram_details[index].capacity || 0,
          shelf_life,
          product_sku,
        };
      }
    });

    const productData = {};
    const capacityData = {}; // Track capacity sums
    const productList = planogram_details.filter((x) => x.product_id);
    
    const tableData = productList.map((x) => {
      productData[x.product_id] = (productData[x.product_id] || 0) + (x.stock || 0);
      capacityData[x.product_id] = (capacityData[x.product_id] || 0) + parseInt(x.capacity || 0);
      return { 
        ...x, 
        stock: productData[x.product_id],
        total_capacity: capacityData[x.product_id] // Save the running total
      };
    });
    const result = _.reverse(_.uniqBy(_.reverse([...tableData]), "product_id"));
    setDeviceList(result);
  };

  const handleSort = (order) => {
    let list = _.cloneDeep(deviceTypeList);
    const sortorder = order?.split("-")?.length > 1 ? "asc" : "desc";
    let value =
      order?.split("-")?.length > 1
        ? order?.split("-")[1]
        : order?.split("-")[0];
    if (value === "stock_capacity" || value === "inventory") {
      value = "stock";
    }
    list = _.orderBy(
      list,
      [
        (item) =>
          isNaN(item[value]) ? item[value].toLowerCase() : item[value],
      ],
      [sortorder]
    );
    setDeviceList(list);
  };

  useEffect(() => {
    setLoader(true);
    if (device?.results?.length) {
      const deviceData = device?.results?.[0] || {};

      if (deviceData?.is_m_series) {
        let modulesObject = deviceData.modules.map((item, index) => {
          return { label: `Module ${index + 1}`, value: item };
        });
        setMSeriesModules(modulesObject);
        setMSeriesSelectedModule(deviceData.modules[0]);
        getInventoryDetailsFromServer(
          deviceData.modules.length,
          deviceData.modules[0]
        );
      } else {
        getInventoryDetailsFromServer();
      }
    }
  }, [device]);

  const onModuleChange = (val) => {
    setMSeriesSelectedModule(val.value);
    getInventoryDetails(val.value);
  };

  const handleExport = async () => {
    setLoader(true);

    window.axiosIns
      .post(
        "/export-to-excel",
        {
          data: _.map(deviceTypeList, (x) => ({
            "Product Id": x.product_id || "---",
            "Product SKU": x.product_sku || "---",
            "Product Name": x.product_name || "---",
            "Stock": x.stock || "0",
            "Total Capacity": x.total_capacity || "0", // --- Added to Excel data
            "Sold-out Status": getInventoryLabel(x?.stock, x?.total_capacity, true), // Fixed mapping to use total_capacity
          })),
          header_map: {
            "Product Id": "Product ID",
            "Product SKU": "Product SKU",
            "Product Name": "Product Name",
            "Stock": "Stock",
            "Total Capacity": "Total Capacity", // --- Added to Excel headers
            "Sold-out Status": "Sold-out Status",
          },
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        setLoader(false);
        enqueueSnackbar("Successfully exported");

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Inventory Per Product-Summary-${moment().format("MM-DD-YYYY")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setLoader(false);
        if (err.detail) {
          enqueueSnackbar(err.detail);
        } else {
          handleServerErrors(
            err,
            enqueueSnackbar,
            "Could not export to excel. Try again."
          );
        }
      });
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <ContentHeader title="" />
      <Card
        style={{ margin: "0px 20px 20px 20px" }}
        variant="outlined"
        className={classes.headerToolbar}
      >
        <Grid
          style={{
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
          }}
          container
        >
          <Grid xs={12} sm={12} md={4}>
            <Box display="flex" alignItems="center">
              <Typography style={{ marginRight: 10 }} variant="subtitle2">
                <strong>VM Serial : </strong>
                {location?.state?.serial_number || ""}
              </Typography>
            </Box>
          </Grid>
          <Grid xs={12} sm={12} md={4}>
            <Typography variant="subtitle2">
              <strong>VM Name : </strong>
              {location?.state?.vm_name || ""}
            </Typography>
          </Grid>
          {mSeriesSelectedModule && (
            <Grid
              
              xs={12}
              sm={12}
              md={4}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <Typography variant="subtitle2" style={{ marginRight: 5 }}>
                <strong>Module : </strong>
              </Typography>
              <MSeriesModulesDropDown
                onChange={(val) => onModuleChange(val)}
                value={mSeriesSelectedModule}
                defaultValue={mSeriesModules[0]}
                options={mSeriesModules}
                size="small"
                backgroundColor="white"
              />
            </Grid>
          )}
        </Grid>
      </Card>

      <div className={classes.toolbar}>
        <div className={classes.crudButtons}>
          <Button
            style={{ minWidth: 100 }}
            size="small"
            className="text-red mr-1"
            onClick={() =>
              navigate(`/inventory/inventory-details/${deviceId}?tab=stock-per-vm`, {
                state: {
                  serial_number: location?.state?.serial_number,
                  vm_name: location?.state?.vm_name,
                  oro_id: deviceId,
                },
              })
            }
            variant="contained"
            color="primary"
          >
            Close
          </Button>
          {current_user.type !== "SU" && (
            <ExportButton
              className="mr-3"
              label="Export"
              onClick={handleExport}
            />
          )}
        </div>
      </div>

      <div className={classes.content}>
        <TableGenerator
          disablePagination={true}
          searchColumnsFilter={true}
          sensorTable={true}
          searchQuery={""}
          fields={_flatten(fields)}
          data={deviceTypeList}
          loader={loader}
          onChangePage={(page) => console.log(page)}
          initialSort={"rack_number"}
          backendPagination={true}
          currentPage={page}
          handleSortChange={(ordering) => {
            handleSort(ordering);
          }}
          onPageChange={(page) => {
            setPage(page);
          }}
          onRowPerPageChange={() => {}}
          dataCount={deviceTypeList?.length}
          selectedRecords={modulesSelected}
          rowOnePage={2500}
          onChangeSelected={(modulesSelected) =>
            setModulesSelected(modulesSelected)
          }
        />
      </div>
    </div>
  );
};

export default InventoryPerProduct;